import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowRight,
  Users,
  Target,
  Layers,
  Globe,
  Wrench,
  Code,
} from "lucide-react";

const beliefs = [
  {
    icon: Target,
    title: "Strategy before execution",
    description:
      "We define what the business actually needs before recommending services or building anything.",
  },
  {
    icon: Layers,
    title: "Custom solutions, not templates",
    description:
      "Every business has different goals, users, and challenges. The solution should reflect that.",
  },
  {
    icon: Globe,
    title: "Built for real use",
    description:
      "Whether it is a website, SEO setup, content direction, or custom tool, the work should support real business use.",
  },
  {
    icon: Wrench,
    title: "Support beyond launch",
    description:
      "We stay involved after launch with improvements, fixes, and ongoing support where needed.",
  },
];

const services = [
  {
    icon: Globe,
    title: "Website Development",
    description: "Custom websites built for speed, clarity, and conversion.",
    link: "/services/website-development",
  },
  {
    icon: Target,
    title: "SEO Services",
    description:
      "Search engine optimization services that help the right clients find you.",
    link: "/services/seo-services",
  },
  {
    icon: Users,
    title: "Social Media Marketing",
    description:
      "Content and strategy that help your business stay visible and build trust.",
    link: "/services/social-media-marketing",
  },
  {
    icon: Code,
    title: "Custom Tool Creation",
    description:
      "Dashboards, calculators, and custom digital tools built around specific business needs.",
    link: "/services/custom-tool-creation",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    description:
      "Ongoing updates, fixes, and technical support that keep your website performing well.",
    link: "/services/maintenance-support",
  },
];

export default function AboutPage() {
  return (
    <Layout>
      <SEO
  title="About | Square Trigger"
  description="Know more about Square Trigger to get exceptional Digital Agency services in Vancouver and throughout Canada. Read more."
  ogTitle="About | Square Trigger"
  ogDescription="Know more about Square Trigger to get exceptional Digital Agency services in Vancouver and throughout Canada. Read more."
  ogSiteName="Square Trigger About us"
  ogImage="https://squaretrigger.com/logo3.webp"
  keywords={SEO_KEYWORDS.about}
/>

     {/* Hero Section */}
<section className="relative overflow-hidden min-h-[calc(100vh-84px)] lg:min-h-[calc(100vh-92px)]">
  <div className="absolute inset-0">
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
    style={{ backgroundImage: "url('/about.webp')" }}
  />

 
  <div className="absolute inset-0 bg-gradient-to-br from-foreground/88 via-foreground/72 to-foreground/45" />

          <motion.div
            className="absolute top-12 right-8 hidden h-56 w-56 rounded-full opacity-30 sm:block md:top-16 md:right-16 md:h-80 md:w-80"
            style={{
              background:
                "radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)",
            }}
            animate={{ scale: [1, 1.2, 1], x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />

          <motion.div
            className="absolute bottom-10 left-8 hidden h-44 w-44 rounded-full opacity-20 sm:block md:bottom-16 md:left-16 md:h-64 md:w-64"
            style={{
              background:
                "radial-gradient(circle, hsl(var(--secondary)) 0%, transparent 70%)",
            }}
            animate={{ scale: [1, 1.3, 1], x: [0, -20, 0], y: [0, 30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 sm:px-4">
                  <Users className="h-4 w-4 text-primary-foreground" />
                  <span className="text-sm font-medium text-primary-foreground">
                    About Us
                  </span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                A Digital Agency in Vancouver Built Around Real{" "}
                <span className="gradient-text">Business Needs</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-3 max-w-[640px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                Square Trigger helps businesses grow through website development,
                SEO services, social media marketing, custom tool creation, and
                ongoing support — built around what the business actually needs,
                not a generic package.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mx-auto m-0 mb-6 max-w-[640px] text-[0.95rem] italic leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We focus on practical digital solutions that support long-term growth,
                stronger visibility, and better online performance.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link
                    to="/services/website-development"
                    className="flex items-center justify-center gap-2"
                  >
                    View Our Services
                  </Link>
                </Button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                className="mx-auto m-0 mt-[14px] max-w-[520px] text-[0.88rem] leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                Every business is different, so our approach is shaped by your goals,
                timeline, and the kind of support you actually need.
              </motion.p>
            </div>

<div className="hidden lg:flex items-center justify-center min-h-[420px]">
  <div className="h-[420px] w-full max-w-[520px]" />
</div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Who We Are
            </span>

            <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
              We Start With the Problem. Then We Build the Right Solution.
            </h2>

            <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              Square Trigger is a digital agency in Vancouver that works with
              businesses that need{" "}
              <Link
                to="/services/website-development"
                className="text-primary hover:underline"
              >
                stronger websites
              </Link>
              ,{" "}
              <Link
                to="/services/seo-services"
                className="text-primary hover:underline"
              >
                better visibility
              </Link>
              ,{" "}
              <Link
                to="/services/social-media-marketing"
                className="text-primary hover:underline"
              >
                clearer messaging
              </Link>
              , and smarter{" "}
              <Link
                to="/services/custom-tool-creation"
                className="text-primary hover:underline"
              >
                digital systems
              </Link>
              .
            </p>

            <div className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8">
              <p className="mx-auto max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                We do not lead with one-size-fits-all packages. We start by
                understanding what is slowing the business down online, then
                build the right solution around that need.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-14 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              What We Believe
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              Good Digital Work Starts With Clarity
            </h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {beliefs.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="flex flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary-soft">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>

                  <h3 className="mb-2 font-semibold text-foreground">
                    {item.title}
                  </h3>

                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {item.title === "Built for real use" ? (
                      <>
                        Whether it is a{" "}
                        <Link
                          to="/services/website-development"
                          className="text-primary hover:underline"
                        >
                          website
                        </Link>
                        ,{" "}
                        <Link
                          to="/services/seo-services"
                          className="text-primary hover:underline"
                        >
                          SEO setup
                        </Link>
                        ,{" "}
                        <Link
                          to="/services/social-media-marketing"
                          className="text-primary hover:underline"
                        >
                          content direction
                        </Link>
                        , or{" "}
                        <Link
                          to="/services/custom-tool-creation"
                          className="text-primary hover:underline"
                        >
                          custom tool
                        </Link>
                        , the work should support real business use.
                      </>
                    ) : item.title === "Support beyond launch" ? (
                      <>
                        We stay involved after launch with improvements, fixes,
                        and{" "}
                        <Link
                          to="/services/maintenance-support"
                          className="text-primary hover:underline"
                        >
                          ongoing support
                        </Link>{" "}
                        where needed.
                      </>
                    ) : (
                      item.description
                    )}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              What We Do
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              The Services Behind the <span className="gradient-text">Strategy</span>
            </h2>
            <p className="mx-auto mt-4 max-w-[900px] leading-relaxed text-background/70">
              We help businesses strengthen their digital presence through
              focused services that work better together, not in isolation.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="h-full"
                >
                  <Card className="flex h-full flex-col items-center border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:text-left">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="mb-3 text-xl font-display font-bold text-background">
                      {service.title}
                    </h3>

                    <p className="mb-5 leading-relaxed text-background/70">
                      {service.description}
                    </p>

                    <Link
                      to={service.link}
                      className="mt-auto inline-flex items-center gap-2 text-primary hover:underline"
                    >
                      Learn More
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
          >
            <div className="mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
                Ready to Work With a Digital Agency That Builds Around Your
                Business?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Let&apos;s talk about what your business needs online and how we
                can help build the right solution.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="gradient"
                  size="lg"
                  asChild
                  className="group w-full justify-center sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center"
                  >
                    Contact Us
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}